<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SegmentedControl from 'dashboard/components-next/segmented-control/SegmentedControl.vue';
import PhraseShortcuts from './PhraseShortcuts.vue';
import {
  BUTTON_TYPES,
  CODE_LIMIT,
  PHONE_LIMIT,
  TEXT_LIMIT,
  URL_LIMIT,
  URL_VARIABLE,
  buttonErrors,
  canAddButton,
  exampleUrl,
  insertButton,
  newButton,
  splitUrlVariable,
  withinButtonLimits,
} from '../helpers/templateButtons';

const props = defineProps({
  category: { type: String, required: true },
});

// A button text being pointed at, for the preview: { index, text }, or null.
const emit = defineEmits(['preview']);

const buttons = defineModel({ type: Array, required: true });

const { t } = useI18n();

const BUTTON_ICONS = {
  QUICK_REPLY: 'i-woot-quick-reply',
  URL: 'i-lucide-external-link',
  PHONE_NUMBER: 'i-lucide-phone',
  COPY_CODE: 'i-lucide-copy',
};

const typeLabels = computed(() => ({
  QUICK_REPLY: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.TYPE_QUICK_REPLY'),
  URL: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.TYPE_URL'),
  PHONE_NUMBER: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.TYPE_PHONE_NUMBER'),
  COPY_CODE: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.TYPE_COPY_CODE'),
}));

const typeHints = computed(() => ({
  QUICK_REPLY: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.HINT_QUICK_REPLY'),
  URL: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.HINT_URL'),
  PHONE_NUMBER: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.HINT_PHONE_NUMBER'),
  COPY_CODE: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.HINT_COPY_CODE'),
}));

// Ready-made button texts; the ones that sell only in marketing templates.
const textPhrases = computed(() => {
  const isMarketing = props.category === 'MARKETING';
  return {
    QUICK_REPLY: [
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.YES'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.NO'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.CONFIRM'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.RESCHEDULE'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.AGENT'),
      ...(isMarketing
        ? [t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.STOP')]
        : []),
    ],
    URL: [
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.ORDER'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.INVOICE'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.DETAILS'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.PORTAL'),
      ...(isMarketing
        ? [t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.BUY')]
        : []),
    ],
    PHONE_NUMBER: [
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.CALL'),
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.SHORTCUTS.BUTTON.STORE'),
    ],
  };
});

const errorMessages = computed(() => ({
  TEXT_LENGTH: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.TEXT_LENGTH', {
    limit: TEXT_LIMIT,
  }),
  CODE_LENGTH: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.CODE_LENGTH', {
    limit: CODE_LIMIT,
  }),
  URL_FORMAT: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.URL_FORMAT'),
  URL_VARIABLE: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.URL_VARIABLE', {
    variable: URL_VARIABLE,
  }),
  URL_EXAMPLE_SPACES: t(
    'WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.URL_EXAMPLE_SPACES'
  ),
  PHONE_FORMAT: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.PHONE_FORMAT'),
  CODE_CATEGORY: t(
    'WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.CODE_CATEGORY'
  ),
}));

const linkOptions = computed(() => [
  {
    value: 'FIXED',
    label: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.LINK_FIXED'),
  },
  {
    value: 'DYNAMIC',
    label: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.LINK_DYNAMIC'),
  },
]);

const errorsFor = button => buttonErrors(button, props.category);

const fieldMessage = (button, field, fallback = '') => {
  const error = errorsFor(button)[field];
  return error ? errorMessages.value[error] : fallback;
};

const fieldType = (button, field) =>
  errorsFor(button)[field] ? 'error' : 'info';

const addableTypes = computed(() =>
  BUTTON_TYPES.filter(type => canAddButton(buttons.value, type, props.category))
);

// WhatsApp Web and desktop skip these buttons, so the agent's own screen shows a bare message.
const hidesOnDesktop = computed(
  () =>
    buttons.value.length >= 4 ||
    (buttons.value.some(button => button.type === 'QUICK_REPLY') &&
      buttons.value.some(button => button.type !== 'QUICK_REPLY'))
);

const addButton = type => {
  buttons.value = insertButton(buttons.value, newButton(type));
};

const removeButton = index => {
  buttons.value = buttons.value.filter((_, position) => position !== index);
  emit('preview', null);
};

const onUrlInput = (button, value) => {
  const { url, dynamic } = splitUrlVariable(value);
  button.url = url;
  if (dynamic) button.dynamic = true;
};
</script>

<template>
  <section class="flex flex-col gap-4">
    <p class="m-0 text-sm text-n-slate-11">
      {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.INTRO') }}
    </p>

    <div
      v-for="(button, index) in buttons"
      :key="index"
      class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
    >
      <div class="flex items-center justify-between gap-2">
        <span
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <Icon
            :icon="BUTTON_ICONS[button.type]"
            class="size-4 text-n-teal-11"
          />
          {{ typeLabels[button.type] }}
        </span>
        <NextButton
          v-tooltip="t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.REMOVE_BUTTON')"
          type="button"
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash-2"
          @click="removeButton(index)"
        />
      </div>

      <Input
        v-if="button.type !== 'COPY_CODE'"
        v-model="button.text"
        :label="t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.TEXT')"
        :placeholder="
          button.type === 'QUICK_REPLY'
            ? t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.TEXT_PLACEHOLDER_REPLY')
            : t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.TEXT_PLACEHOLDER')
        "
        :maxlength="TEXT_LIMIT"
        :message="
          fieldMessage(button, 'text', `${button.text.length}/${TEXT_LIMIT}`)
        "
        :message-type="fieldType(button, 'text')"
      />
      <PhraseShortcuts
        v-if="button.type !== 'COPY_CODE'"
        :phrases="textPhrases[button.type]"
        :model-value="button.text"
        @apply="button.text = $event"
        @preview="emit('preview', $event ? { index, text: $event } : null)"
      />

      <template v-if="button.type === 'URL'">
        <div class="flex flex-col gap-2">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="text-heading-3 text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.LINK') }}
            </span>
            <SegmentedControl
              :model-value="button.dynamic ? 'DYNAMIC' : 'FIXED'"
              :options="linkOptions"
              @update:model-value="button.dynamic = $event === 'DYNAMIC'"
            />
          </div>
          <div class="flex items-start gap-2">
            <Input
              :model-value="button.url"
              class="flex-1"
              type="url"
              :placeholder="
                button.dynamic
                  ? t(
                      'WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.URL_BASE_PLACEHOLDER'
                    )
                  : t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.URL_PLACEHOLDER')
              "
              :maxlength="URL_LIMIT"
              :message="fieldMessage(button, 'url')"
              :message-type="fieldType(button, 'url')"
              @update:model-value="onUrlInput(button, $event)"
            />
            <span
              v-if="button.dynamic"
              v-tooltip="t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.URL_CHIP')"
              class="flex items-center flex-shrink-0 h-10 px-2.5 font-mono text-sm rounded-lg bg-n-teal-3 text-n-teal-11"
            >
              {{ URL_VARIABLE }}
            </span>
          </div>
        </div>

        <div
          v-if="button.dynamic"
          class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-2"
        >
          <p class="m-0 text-xs text-n-slate-11">
            {{
              t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.DYNAMIC_HELP', {
                variable: URL_VARIABLE,
              })
            }}
          </p>
          <Input
            v-model="button.example"
            :label="
              t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.URL_EXAMPLE', {
                variable: URL_VARIABLE,
              })
            "
            :placeholder="
              t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.URL_EXAMPLE_PLACEHOLDER')
            "
            :message="fieldMessage(button, 'example')"
            :message-type="fieldType(button, 'example')"
          />
          <p
            v-if="button.url && button.example"
            class="m-0 text-xs break-all text-n-slate-11"
          >
            {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.URL_RESULT') }}
            <span class="font-mono text-n-slate-12">{{
              exampleUrl(button)
            }}</span>
          </p>
        </div>
      </template>

      <Input
        v-if="button.type === 'PHONE_NUMBER'"
        v-model="button.phoneNumber"
        :label="t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.PHONE')"
        type="tel"
        :placeholder="
          t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.PHONE_PLACEHOLDER')
        "
        :maxlength="PHONE_LIMIT"
        :message="
          fieldMessage(
            button,
            'phoneNumber',
            t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.PHONE_HELP')
          )
        "
        :message-type="fieldType(button, 'phoneNumber')"
      />

      <Input
        v-if="button.type === 'COPY_CODE'"
        v-model="button.example"
        :label="t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.CODE_EXAMPLE')"
        :placeholder="
          t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.CODE_PLACEHOLDER')
        "
        :maxlength="CODE_LIMIT"
        :message="
          fieldMessage(
            button,
            'example',
            t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.CODE_HELP')
          )
        "
        :message-type="fieldType(button, 'example')"
      />
    </div>

    <!-- Only a draft from Captain can go over: the buttons below never offer more than Meta takes. -->
    <p
      v-if="!withinButtonLimits(buttons)"
      class="flex gap-2 m-0 text-xs text-n-ruby-9"
    >
      <Icon icon="i-lucide-circle-alert" class="flex-shrink-0 size-3.5 mt-px" />
      {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ERRORS.LIMITS') }}
    </p>

    <div v-if="addableTypes.length" class="flex flex-col gap-2">
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ADD_BUTTON') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <NextButton
          v-for="type in addableTypes"
          :key="type"
          v-tooltip="typeHints[type]"
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          :icon="BUTTON_ICONS[type]"
          @click="addButton(type)"
        >
          {{ typeLabels[type] }}
        </NextButton>
      </div>
    </div>

    <p v-if="hidesOnDesktop" class="flex gap-2 m-0 text-xs text-n-slate-11">
      <Icon icon="i-lucide-monitor" class="flex-shrink-0 size-3.5 mt-px" />
      {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.DESKTOP_NOTICE') }}
    </p>
  </section>
</template>
